import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Check, AlertCircle, Loader2 } from 'lucide-react';

/**
 * SPA Pflegeberatung — Schulungs-Anforderungs-Widget (§ 45 SGB XI).
 *
 * 6 Schritte:
 *   1. Themen-Auswahl (mind. eines)
 *   2. § 37.3-Beratungsbesuch in laufendem Halbjahr genutzt? Ja/Nein
 *   3. Persoenliche Daten (Wer fragt? + Daten — bei 'andere' zwei Bloecke)
 *   4. Anschrift (Patient)
 *   5. Versicherung (Pflegegrad, GKV/PKV-Toggle, Pflegekasse-Auswahl,
 *      Versichertennummer mit Format-Pruefung)
 *   6. Datenschutz + Submit
 *
 * Eingebettet via Iframe auf spa-pflegeberatung.de/schulung-und-kurse.
 * Sendet postMessage 'widget-resize' fuer dynamische Iframe-Hoehe.
 *
 * Backend speichert die Anfrage als Kunde mit quelle='schulung_anfrage'
 * — Hendrik sieht sie direkt im Berater-Dashboard wie jeden anderen
 * Beratungskunden, mit DSGVO-DOI-Marker bis bestaetigt.
 */

const THEMEN = [
  { key: 'praevention',     icon: '🛡️', titel: 'Prävention und Selbstpflege' },
  { key: 'lagerung',        icon: '🛏️', titel: 'Lagerung und Transfer' },
  { key: 'mobilitaet',      icon: '🚶', titel: 'Mobilität' },
  { key: 'essen',           icon: '🍽️', titel: 'Essen und Trinken' },
  { key: 'koerperpflege',   icon: '🧴', titel: 'Körperpflege' },
  { key: 'pflegehilfsmittel', icon: '🦽', titel: 'Umgang mit Pflegehilfsmitteln' },
  { key: 'demenz',          icon: '🧠', titel: 'Demenzielle Herausforderungen' },
  { key: 'mundpflege',      icon: '🦷', titel: 'Mundpflege' },
  { key: 'wahrnehmung',     icon: '👁️', titel: 'Förderung der Wahrnehmung' },
  { key: 'kontinenz',       icon: '🚿', titel: 'Kontinenzpflege' },
  { key: 'notfaelle',       icon: '🚨', titel: 'Notfälle in der häuslichen Pflege' },
];

const ANREDEN = ['Frau', 'Herr', 'Divers'];

const PRIMARY = '#E83A7A';
const PRIMARY_DARK = '#C42569';
const ANTHRAZIT = '#3D4D5C';

const initialState = {
  themen: [],
  beratungsbesuch_genutzt: null,
  anfrage_fuer: 'selbst',          // 'selbst' | 'angehoeriger'

  anfragender_anrede: 'Frau',
  anfragender_vorname: '',
  anfragender_nachname: '',
  anfragender_geburtsdatum: '',
  anfragender_email: '',
  anfragender_telefon: '',

  patient_anrede: 'Frau',
  patient_vorname: '',
  patient_nachname: '',
  patient_geburtsdatum: '',

  patient_strasse: '',
  patient_plz: '',
  patient_ort: '',

  patient_pflegegrad: 1,
  patient_versicherungsart: 'GKV',
  patient_pflegekasse: '',
  patient_versichertennummer: '',

  datenschutz_akzeptiert: false,
};

const inputBase = {
  width: '100%', padding: '0.7rem 0.9rem', fontSize: '0.95rem',
  border: '1px solid #d1d5db', borderRadius: '8px',
  background: '#fff', color: ANTHRAZIT, fontFamily: 'inherit',
};
const labelBase = {
  display: 'block', fontSize: '0.85rem', fontWeight: 600,
  color: ANTHRAZIT, marginBottom: '0.4rem',
};
const errorBorder = { borderColor: '#dc2626', background: '#FEF2F2' };

const btnPrimary = {
  background: PRIMARY, color: '#fff', border: 'none',
  padding: '0.85rem 1.6rem', borderRadius: '999px',
  fontSize: '1rem', fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
  fontFamily: 'inherit',
};
const btnGhost = {
  background: 'transparent', color: ANTHRAZIT, border: '1px solid #d1d5db',
  padding: '0.7rem 1.4rem', borderRadius: '999px',
  fontSize: '0.95rem', fontWeight: 500, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
  fontFamily: 'inherit',
};

const STEP_COUNT = 6;

export default function SchulungAnfrageWidget() {
  const [schritt, setSchritt] = useState(1);
  const [data, setData] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [krankenkassen, setKrankenkassen] = useState([]);
  const [insuranceSearch, setInsuranceSearch] = useState('');
  const [showInsuranceList, setShowInsuranceList] = useState(false);
  const containerRef = useRef(null);
  const insuranceDropdownRef = useRef(null);

  useEffect(() => {
    fetch('/api/pflegekassen/public')
      .then((r) => r.ok ? r.json() : [])
      .then((arr) => Array.isArray(arr) && setKrankenkassen(arr))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (insuranceDropdownRef.current && !insuranceDropdownRef.current.contains(e.target)) {
        setShowInsuranceList(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    const sendeHoehe = () => {
      if (window.parent !== window && containerRef.current) {
        window.parent.postMessage(
          { type: 'widget-resize', height: containerRef.current.offsetHeight },
          '*'
        );
      }
    };
    sendeHoehe();
    const obs = new ResizeObserver(sendeHoehe);
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, [schritt, submitted]);

  const setFeld = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const themaToggle = (key) => {
    setData((d) => ({
      ...d,
      themen: d.themen.includes(key)
        ? d.themen.filter((t) => t !== key)
        : [...d.themen, key],
    }));
  };

  // Validierungs-Helpers (analog BookingWidget)
  const isPlzValid = (plz) => /^\d{5}$/.test((plz || '').trim());
  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
  const isPhoneValid = (phone) => {
    const t = (phone || '').trim();
    if (!t) return false;
    if (!/^[\d\s+()\/\-]+$/.test(t)) return false;
    return (t.match(/\d/g) || []).length >= 5;
  };
  const isInsuranceNumberValid = () => {
    const v = (data.patient_versichertennummer || '').trim();
    if (data.patient_versicherungsart === 'PKV') return Boolean(v);
    return /^[A-Z]\d{9}$/.test(v);
  };

  const filteredInsurances = krankenkassen.filter(
    (k) => k.typ === data.patient_versicherungsart &&
           k.name.toLowerCase().includes(insuranceSearch.toLowerCase())
  );

  // Validierung pro Schritt
  const istSchrittValide = () => {
    if (schritt === 1) return data.themen.length > 0;
    if (schritt === 2) return data.beratungsbesuch_genutzt !== null;

    if (schritt === 3) {
      const a = data;
      const anfragender = (
        a.anfragender_vorname.trim() && a.anfragender_nachname.trim() &&
        isEmailValid(a.anfragender_email) && isPhoneValid(a.anfragender_telefon)
      );
      if (a.anfrage_fuer === 'selbst') {
        // Bei 'selbst' brauchen wir noch Geburtsdatum (das gehoert zum Patient/Anfragender)
        return anfragender && Boolean(a.anfragender_geburtsdatum);
      }
      // Bei 'andere': Patient als zweiter Block
      const patient = (
        a.patient_vorname.trim() && a.patient_nachname.trim() && a.patient_geburtsdatum
      );
      return anfragender && patient;
    }

    if (schritt === 4) {
      return Boolean(data.patient_strasse.trim() &&
                     isPlzValid(data.patient_plz) &&
                     data.patient_ort.trim());
    }

    if (schritt === 5) {
      return Boolean(data.patient_pflegegrad &&
                     data.patient_versicherungsart &&
                     data.patient_pflegekasse.trim() &&
                     isInsuranceNumberValid());
    }

    if (schritt === 6) return data.datenschutz_akzeptiert;
    return false;
  };

  const weiter = () => {
    if (!istSchrittValide()) return;
    if (schritt < STEP_COUNT) setSchritt(schritt + 1);
    else absenden();
  };
  const zurueck = () => schritt > 1 && setSchritt(schritt - 1);

  // Bei 'selbst' synchronisieren wir die patient_*-Felder mit den anfragender_*-Feldern
  // beim Submit, damit das Backend einen sauberen Patient-Datensatz bekommt.
  const baueSubmitDaten = () => {
    const d = data;
    const istSelbst = d.anfrage_fuer === 'selbst';
    return {
      themen: d.themen,
      beratungsbesuch_genutzt: d.beratungsbesuch_genutzt,
      anfrage_fuer: d.anfrage_fuer,
      anfragender_anrede: d.anfragender_anrede,
      anfragender_vorname: d.anfragender_vorname,
      anfragender_nachname: d.anfragender_nachname,
      anfragender_email: d.anfragender_email,
      anfragender_telefon: d.anfragender_telefon,
      patient_anrede: istSelbst ? d.anfragender_anrede : d.patient_anrede,
      patient_vorname: istSelbst ? d.anfragender_vorname : d.patient_vorname,
      patient_nachname: istSelbst ? d.anfragender_nachname : d.patient_nachname,
      patient_geburtsdatum: istSelbst ? d.anfragender_geburtsdatum : d.patient_geburtsdatum,
      patient_strasse: d.patient_strasse,
      patient_plz: d.patient_plz,
      patient_ort: d.patient_ort,
      patient_pflegegrad: d.patient_pflegegrad,
      patient_versicherungsart: d.patient_versicherungsart,
      patient_pflegekasse: d.patient_pflegekasse,
      patient_versichertennummer: d.patient_versichertennummer,
      datenschutz_akzeptiert: d.datenschutz_akzeptiert,
    };
  };

  const absenden = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const r = await fetch('/api/schulung/anfrage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(baueSubmitDaten()),
      });
      if (!r.ok) {
        const fehler = await r.text();
        throw new Error(`Server ${r.status}: ${fehler}`);
      }
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message || 'Übertragung fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  // ===================================================================
  // Render
  // ===================================================================

  if (submitted) {
    return (
      <div ref={containerRef} style={{ fontFamily: 'system-ui, -apple-system, sans-serif',
                                       maxWidth: 720, margin: '0 auto', padding: '2rem 1rem' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
                      padding: '2.5rem 2rem', textAlign: 'center',
                      boxShadow: '0 4px 24px rgba(232,58,122,0.08)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#9CC83C',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: '1rem' }}>
            <Check size={32} color="#fff" strokeWidth={3} />
          </div>
          <h2 style={{ color: ANTHRAZIT, marginTop: 0 }}>Anfrage wurde verschickt</h2>
          <p style={{ color: '#6B7785' }}>
            Wir haben Ihnen gerade eine E-Mail an <strong>{data.anfragender_email}</strong> geschickt.
            Bitte klicken Sie kurz auf den Bestätigungs-Link in der E-Mail — danach melden wir uns
            mit konkreten Terminvorschlägen.
          </p>
          <p style={{ color: '#6B7785', fontSize: '0.9rem', marginTop: '1.5rem' }}>
            Falls die E-Mail nicht ankommt, prüfen Sie bitte auch den Spam-Ordner.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ fontFamily: 'system-ui, -apple-system, sans-serif',
                                     maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem' }}>

      {/* Schritt-Indikator (6 Streifen) */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.5rem', justifyContent: 'center' }}>
        {Array.from({ length: STEP_COUNT }, (_, i) => (
          <div key={i} style={{
            flex: 1, height: 6, borderRadius: 3, maxWidth: 80,
            background: i + 1 <= schritt ? PRIMARY : '#e5e7eb',
            transition: 'background 0.2s',
          }} />
        ))}
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16,
                    padding: '2rem', boxShadow: '0 4px 24px rgba(232,58,122,0.08)' }}>

        {/* Schritt 1: Themen */}
        {schritt === 1 && (
          <>
            <Eyebrow s={1} of={STEP_COUNT} />
            <h2 style={{ color: ANTHRAZIT, marginTop: '0.5rem' }}>Welche Themen interessieren Sie?</h2>
            <p style={{ color: '#6B7785' }}>Mehrere Themen sind möglich.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                          gap: '0.75rem', marginTop: '1.5rem' }}>
              {THEMEN.map((t) => {
                const aktiv = data.themen.includes(t.key);
                return (
                  <button key={t.key} type="button" onClick={() => themaToggle(t.key)}
                    style={{
                      padding: '1rem',
                      border: aktiv ? `2px solid ${PRIMARY}` : '1px solid #e5e7eb',
                      borderRadius: 12, background: aktiv ? '#FCEEF4' : '#fff',
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      color: ANTHRAZIT, fontSize: '0.95rem',
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                    }}>
                    <span style={{ fontSize: '1.4rem' }}>{t.icon}</span>
                    <span style={{ flex: 1 }}>{t.titel}</span>
                    {aktiv && <Check size={18} color={PRIMARY} />}
                  </button>
                );
              })}
            </div>
          </>
        )}

        {/* Schritt 2: Beratungsbesuch */}
        {schritt === 2 && (
          <>
            <Eyebrow s={2} of={STEP_COUNT} />
            <h2 style={{ color: ANTHRAZIT, marginTop: '0.5rem' }}>Beratungsbesuch im laufenden Halbjahr</h2>
            <p style={{ color: '#6B7785' }}>
              Hat die zu pflegende Person den Beratungsbesuch nach § 37.3 SGB XI im laufenden
              Halbjahr schon in Anspruch genommen?
            </p>
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' }}>
              {[
                { v: true, label: 'Ja, schon erfolgt' },
                { v: false, label: 'Nein, noch nicht' },
              ].map((opt) => {
                const aktiv = data.beratungsbesuch_genutzt === opt.v;
                return (
                  <button key={String(opt.v)} type="button"
                    onClick={() => setFeld('beratungsbesuch_genutzt', opt.v)}
                    style={{
                      flex: 1, minWidth: 200, padding: '1.5rem',
                      border: aktiv ? `2px solid ${PRIMARY}` : '1px solid #e5e7eb',
                      borderRadius: 12, background: aktiv ? '#FCEEF4' : '#fff',
                      cursor: 'pointer', fontFamily: 'inherit',
                      color: ANTHRAZIT, fontSize: '1.05rem', fontWeight: 500,
                    }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p style={{ color: '#6B7785', fontSize: '0.85rem', marginTop: '1.5rem',
                        background: '#FAFBFC', padding: '0.8rem 1rem', borderRadius: 8 }}>
              <strong>Hinweis:</strong> Pflegeschulungen nach § 45 SGB XI sind ein
              <strong> kostenloses Angebot Ihrer Pflegeversicherung</strong>. Die folgenden
              Daten benötigen wir für die Abrechnung mit der Pflegekasse — für Sie
              entstehen keine Kosten.
            </p>
          </>
        )}

        {/* Schritt 3: Persoenliche Daten */}
        {schritt === 3 && (
          <>
            <Eyebrow s={3} of={STEP_COUNT} />
            <h2 style={{ color: ANTHRAZIT, marginTop: '0.5rem' }}>Persönliche Daten</h2>

            <div style={{ marginTop: '1rem' }}>
              <label style={labelBase}>Sie fragen die Schulung an für …</label>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { v: 'selbst', label: 'mich selbst' },
                  { v: 'angehoeriger', label: 'eine pflegebedürftige Person' },
                ].map((opt) => {
                  const aktiv = data.anfrage_fuer === opt.v;
                  return (
                    <button key={opt.v} type="button"
                      onClick={() => setFeld('anfrage_fuer', opt.v)}
                      style={{
                        padding: '0.6rem 1.2rem', borderRadius: 999,
                        border: aktiv ? `2px solid ${PRIMARY}` : '1px solid #d1d5db',
                        background: aktiv ? '#FCEEF4' : '#fff', cursor: 'pointer',
                        color: ANTHRAZIT, fontFamily: 'inherit', fontSize: '0.95rem',
                      }}>
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Anfragender (immer als Kontaktperson — bei 'selbst' ist sie auch der Patient) */}
            <h3 style={{ color: ANTHRAZIT, fontSize: '1.1rem', marginTop: '2rem',
                         marginBottom: '0.8rem' }}>
              {data.anfrage_fuer === 'selbst' ? 'Ihre Daten' : 'Ihre Kontaktdaten (anfragende Person)'}
            </h3>
            <PersonenBlock
              prefix="anfragender_"
              data={data}
              setFeld={setFeld}
              showGeburtsdatum={data.anfrage_fuer === 'selbst'}
              showEmailTel={true}
              isEmailValid={isEmailValid}
              isPhoneValid={isPhoneValid}
            />

            {/* Patient nur bei 'andere' */}
            {data.anfrage_fuer === 'angehoeriger' && (
              <>
                <h3 style={{ color: ANTHRAZIT, fontSize: '1.1rem', marginTop: '2rem',
                             marginBottom: '0.4rem' }}>
                  Pflegebedürftige Person
                </h3>
                <p style={{ color: '#6B7785', fontSize: '0.85rem', marginTop: 0,
                            marginBottom: '0.8rem' }}>
                  Diese Angaben benötigen wir für die Abrechnung mit der Pflegekasse.
                </p>
                <PersonenBlock
                  prefix="patient_"
                  data={data}
                  setFeld={setFeld}
                  showGeburtsdatum={true}
                  showEmailTel={false}
                />
              </>
            )}
          </>
        )}

        {/* Schritt 4: Anschrift */}
        {schritt === 4 && (
          <>
            <Eyebrow s={4} of={STEP_COUNT} />
            <h2 style={{ color: ANTHRAZIT, marginTop: '0.5rem' }}>Anschrift</h2>
            <p style={{ color: '#6B7785' }}>
              Adresse, an der die Schulung stattfindet
              {data.anfrage_fuer === 'selbst' ? '' : ' (Wohnort der pflegebedürftigen Person)'}.
            </p>
            <div style={{ display: 'grid', gap: '0.8rem', marginTop: '1rem' }}>
              <div>
                <label style={labelBase}>Straße und Hausnummer *</label>
                <input style={inputBase} value={data.patient_strasse}
                       onChange={(e) => setFeld('patient_strasse', e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '0.8rem' }}>
                <div>
                  <label style={labelBase}>PLZ *</label>
                  <input style={{
                           ...inputBase,
                           ...(data.patient_plz && !isPlzValid(data.patient_plz) ? errorBorder : {}),
                         }}
                         maxLength={5} value={data.patient_plz}
                         onChange={(e) => setFeld('patient_plz', e.target.value.replace(/\D/g, ''))}
                         placeholder="5-stellig" />
                </div>
                <div>
                  <label style={labelBase}>Ort *</label>
                  <input style={inputBase} value={data.patient_ort}
                         onChange={(e) => setFeld('patient_ort', e.target.value)} />
                </div>
              </div>
            </div>
          </>
        )}

        {/* Schritt 5: Versicherung */}
        {schritt === 5 && (
          <>
            <Eyebrow s={5} of={STEP_COUNT} />
            <h2 style={{ color: ANTHRAZIT, marginTop: '0.5rem' }}>Versicherungsdaten</h2>
            <p style={{ color: '#6B7785' }}>
              Die folgenden Angaben benötigen wir für die Abrechnung mit der Pflegekasse —
              für Sie entstehen keine Kosten.
            </p>
            <div style={{ display: 'grid', gap: '1rem', marginTop: '1rem' }}>
              <div>
                <label style={labelBase}>Pflegegrad *</label>
                <select style={inputBase} value={data.patient_pflegegrad}
                        onChange={(e) => setFeld('patient_pflegegrad', parseInt(e.target.value, 10))}>
                  {[1, 2, 3, 4, 5].map((p) => <option key={p} value={p}>Pflegegrad {p}</option>)}
                </select>
              </div>

              <div>
                <label style={labelBase}>Versicherungsart *</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['GKV', 'PKV'].map((art) => {
                    const aktiv = data.patient_versicherungsart === art;
                    return (
                      <button key={art} type="button"
                        onClick={() => {
                          setFeld('patient_versicherungsart', art);
                          setFeld('patient_pflegekasse', '');
                          setInsuranceSearch('');
                        }}
                        style={{
                          flex: 1, padding: '0.7rem', fontSize: '0.9rem', fontWeight: 600,
                          borderRadius: 8, cursor: 'pointer',
                          border: aktiv ? `2px solid ${PRIMARY}` : '1px solid #d1d5db',
                          background: aktiv ? '#FCEEF4' : '#fff',
                          color: aktiv ? PRIMARY : ANTHRAZIT, fontFamily: 'inherit',
                        }}>
                        {art === 'GKV' ? 'Gesetzlich (GKV)' : 'Privat (PKV)'}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ position: 'relative' }} ref={insuranceDropdownRef}>
                <label style={labelBase}>Name der Pflegekasse *</label>
                <input style={inputBase}
                       placeholder="Bitte auswählen oder Name tippen…"
                       value={insuranceSearch || data.patient_pflegekasse}
                       onChange={(e) => {
                         setInsuranceSearch(e.target.value);
                         setFeld('patient_pflegekasse', e.target.value);
                         setShowInsuranceList(true);
                       }}
                       onFocus={() => setShowInsuranceList(true)} />
                {showInsuranceList && filteredInsurances.length > 0 && (
                  <ul style={{
                    position: 'absolute', zIndex: 50, top: '100%', left: 0, right: 0,
                    marginTop: 4, background: '#fff', border: '1px solid #e5e7eb',
                    borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    maxHeight: 220, overflowY: 'auto', listStyle: 'none', padding: 0,
                  }}>
                    {filteredInsurances.map((kasse, idx) => (
                      <li key={idx}
                          onClick={() => {
                            setFeld('patient_pflegekasse', kasse.name);
                            setInsuranceSearch(kasse.name);
                            setShowInsuranceList(false);
                          }}
                          style={{ padding: '0.7rem 0.9rem', fontSize: '0.9rem',
                                   color: ANTHRAZIT, cursor: 'pointer',
                                   borderBottom: '1px solid #f3f4f6' }}
                          onMouseEnter={(e) => e.currentTarget.style.background = '#FCEEF4'}
                          onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}>
                        {kasse.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div>
                <label style={labelBase}>
                  Versichertennummer *
                  {data.patient_versicherungsart === 'GKV' && (
                    <span style={{ marginLeft: 8, color: '#6B7785', fontWeight: 400 }}>
                      (1 Buchstabe + 9 Ziffern, z.B. A123456789)
                    </span>
                  )}
                </label>
                <input style={{
                         ...inputBase,
                         ...(data.patient_versichertennummer && !isInsuranceNumberValid()
                             ? errorBorder : {}),
                       }}
                       value={data.patient_versichertennummer}
                       onChange={(e) => setFeld('patient_versichertennummer',
                                                e.target.value.toUpperCase())}
                       placeholder={data.patient_versicherungsart === 'GKV' ? 'A123456789' : 'Ihre Versichertennummer'} />
              </div>
            </div>
          </>
        )}

        {/* Schritt 6: Datenschutz */}
        {schritt === 6 && (
          <>
            <Eyebrow s={6} of={STEP_COUNT} />
            <h2 style={{ color: ANTHRAZIT, marginTop: '0.5rem' }}>Bestätigung</h2>

            <div style={{ background: '#FAFBFC', padding: '1rem 1.2rem',
                          borderRadius: 8, marginTop: '1rem', fontSize: '0.9rem' }}>
              <strong>Zusammenfassung:</strong>
              <ul style={{ margin: '0.5rem 0 0 1rem', padding: 0, color: '#6B7785' }}>
                <li>{data.themen.length} Thema/Themen ausgewählt</li>
                <li>Beratungsbesuch laufendes Halbjahr: {data.beratungsbesuch_genutzt ? 'ja' : 'nein'}</li>
                <li>Anfrage für: {data.anfrage_fuer === 'selbst' ? 'mich selbst' : 'andere Person'}</li>
                <li>E-Mail-Bestätigung geht an: <strong>{data.anfragender_email}</strong></li>
              </ul>
            </div>

            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.7rem',
                            marginTop: '1.5rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={data.datenschutz_akzeptiert}
                     onChange={(e) => setFeld('datenschutz_akzeptiert', e.target.checked)}
                     style={{ marginTop: 4, width: 18, height: 18, cursor: 'pointer' }} />
              <span style={{ color: ANTHRAZIT, fontSize: '0.9rem' }}>
                Ich stimme zu, dass meine Daten zur Bearbeitung der Schulungsanfrage
                und zur Abrechnung mit der Pflegekasse verarbeitet werden.
                Die <a href="https://spa-pflegeberatung.de/datenschutz" target="_blank"
                       rel="noopener noreferrer" style={{ color: PRIMARY }}>
                  Datenschutzerklärung
                </a> habe ich gelesen.
              </span>
            </label>

            {submitError && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start',
                            background: '#FEF2F2', color: '#991B1B', padding: '0.8rem 1rem',
                            borderRadius: 8, marginTop: '1rem', fontSize: '0.9rem' }}>
                <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <strong>Anfrage konnte nicht versendet werden.</strong><br />
                  {submitError}
                </div>
              </div>
            )}
          </>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      marginTop: '2rem', gap: '1rem', flexWrap: 'wrap' }}>
          {schritt > 1 ? (
            <button type="button" onClick={zurueck} style={btnGhost} disabled={submitting}>
              <ChevronLeft size={18} /> Zurück
            </button>
          ) : <span />}
          <button type="button" onClick={weiter}
                  disabled={!istSchrittValide() || submitting}
                  style={{
                    ...btnPrimary,
                    opacity: !istSchrittValide() || submitting ? 0.5 : 1,
                    cursor: !istSchrittValide() || submitting ? 'not-allowed' : 'pointer',
                  }}
                  onMouseOver={(e) => { if (!submitting && istSchrittValide()) e.currentTarget.style.background = PRIMARY_DARK; }}
                  onMouseOut={(e) => { e.currentTarget.style.background = PRIMARY; }}>
            {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
            {schritt < STEP_COUNT ? 'Weiter' : (submitting ? 'Wird gesendet …' : 'Anfrage absenden')}
            {schritt < STEP_COUNT && <ChevronRight size={18} />}
          </button>
        </div>
      </div>
    </div>
  );
}


// --- Hilfs-Komponenten -------------------------------------------------

function Eyebrow({ s, of }) {
  return (
    <p style={{ color: PRIMARY, fontSize: '0.8rem', fontWeight: 700,
                letterSpacing: '0.08em', textTransform: 'uppercase', margin: 0 }}>
      Schritt {s} von {of}
    </p>
  );
}

/**
 * Personen-Datenblock — Anrede, Vor-, Nachname, optional Geburtsdatum + Telefon,
 * optional Email. Felder-Prefix bestimmt, ob anfragender_* oder patient_*.
 */
function PersonenBlock({ prefix, data, setFeld, showGeburtsdatum, showEmailTel,
                         isEmailValid, isPhoneValid }) {
  const f = (k) => prefix + k;
  const v = (k) => data[f(k)];

  const errorBorder = { borderColor: '#dc2626', background: '#FEF2F2' };

  return (
    <div style={{ display: 'grid', gap: '0.8rem' }}>
      <div style={{ display: 'grid',
                    gridTemplateColumns: '140px 1fr 1fr', gap: '0.8rem' }}>
        <div>
          <label style={labelBase}>Anrede *</label>
          <select style={inputBase} value={v('anrede')}
                  onChange={(e) => setFeld(f('anrede'), e.target.value)}>
            {ANREDEN.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label style={labelBase}>Vorname *</label>
          <input style={inputBase} value={v('vorname')}
                 onChange={(e) => setFeld(f('vorname'), e.target.value)} />
        </div>
        <div>
          <label style={labelBase}>Nachname *</label>
          <input style={inputBase} value={v('nachname')}
                 onChange={(e) => setFeld(f('nachname'), e.target.value)} />
        </div>
      </div>

      {showGeburtsdatum && showEmailTel && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
          <div>
            <label style={labelBase}>Geburtsdatum *</label>
            <input type="date" style={inputBase} value={v('geburtsdatum')}
                   onChange={(e) => setFeld(f('geburtsdatum'), e.target.value)} />
          </div>
          <div>
            <label style={labelBase}>Telefon *</label>
            <input type="tel" inputMode="tel" placeholder="z.B. 0421 123456"
                   style={{
                     ...inputBase,
                     ...(v('telefon') && isPhoneValid && !isPhoneValid(v('telefon')) ? errorBorder : {}),
                   }}
                   value={v('telefon')}
                   onChange={(e) => setFeld(f('telefon'), e.target.value)} />
          </div>
        </div>
      )}

      {showGeburtsdatum && !showEmailTel && (
        <div>
          <label style={labelBase}>Geburtsdatum *</label>
          <input type="date" style={inputBase} value={v('geburtsdatum')}
                 onChange={(e) => setFeld(f('geburtsdatum'), e.target.value)} />
        </div>
      )}

      {!showGeburtsdatum && showEmailTel && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.8rem' }}>
          <div>
            <label style={labelBase}>Telefon *</label>
            <input type="tel" inputMode="tel" placeholder="z.B. 0421 123456"
                   style={{
                     ...inputBase,
                     ...(v('telefon') && isPhoneValid && !isPhoneValid(v('telefon')) ? errorBorder : {}),
                   }}
                   value={v('telefon')}
                   onChange={(e) => setFeld(f('telefon'), e.target.value)} />
          </div>
          <div>
            <label style={labelBase}>E-Mail *</label>
            <input type="email"
                   style={{
                     ...inputBase,
                     ...(v('email') && isEmailValid && !isEmailValid(v('email')) ? errorBorder : {}),
                   }}
                   value={v('email')}
                   onChange={(e) => setFeld(f('email'), e.target.value)} />
          </div>
        </div>
      )}

      {showEmailTel && showGeburtsdatum && (
        <div>
          <label style={labelBase}>E-Mail *</label>
          <input type="email"
                 style={{
                   ...inputBase,
                   ...(v('email') && isEmailValid && !isEmailValid(v('email')) ? errorBorder : {}),
                 }}
                 value={v('email')}
                 onChange={(e) => setFeld(f('email'), e.target.value)} />
        </div>
      )}
    </div>
  );
}
